import {gsap} from "gsap";
import {Game} from "../Game";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";

interface BitmapTextStyleOptions {
    fontName: string;
    fontSize: number;
    tint: number;
}

export class CountUp extends PIXI.Container {

    public currentCountValue: number;     // Used by greensock. Don't change
    protected _amountText: PIXI.BitmapText;
    protected _anchor: PIXI.Point;
    private readonly _maxWidth: number | undefined;
    private _activeAnimation?: gsap.core.Timeline;
    private _pareCalculatedScale: boolean;
    private _prevValue: number;
    private _endEffect: TimelineSprite;

    constructor(style: BitmapTextStyleOptions, maxWidth: number, className: string, anchor?: PIXI.Point) {
        super();
        this.name = className;

        this.visible = false;

        this._maxWidth = maxWidth;
        this._anchor = anchor ? anchor : new PIXI.Point(0.5, 0.5);
        this._amountText = new PIXI.BitmapText("", style);
        this.position.set(360, 0);

        this._endEffect = new TimelineSprite(Game.assetLoader.getTextures("winlabel/countup/eff_00"));
        this._endEffect.hide();
        this._endEffect.anchor.set(0.5);
        this._endEffect.scale.set(2);
        this._endEffect.blendMode = PIXI.BLEND_MODES.ADD;

        this.addChild(this._amountText, this._endEffect);
    }

    public getReady(yPos: number, alpha: number = 0): void {
        this.visible = true;
        this.y = yPos;

        if (alpha === 0) {
            const tl: gsap.core.Timeline = gsap.timeline();

            tl.to(this, {
                duration: 0.1,
                alpha: 1
            });
        }
    }

    public stopAt(amount: number): void {
        if (this._activeAnimation !== undefined) {
            this._activeAnimation.pause();
            this._activeAnimation.kill();
        }
        delete this._activeAnimation;
        this.currentCountValue = amount;
        this._prevValue = amount;
        this.setAmount(amount);
    }

    public preCalculateScale(from: number, to: number): void {
        this.setScaleToFitMaxWidth(from, to);
        this._pareCalculatedScale = true;
    }

    public getCountAnimation(from: number, to: number, duration: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            onUpdate: () => this.updateText()
        });
        tl.add(() => {
            this.stopAt(from);
            this._activeAnimation = tl; // it has to be here because we are already killing it in the above call.
        });

        if (!this._pareCalculatedScale) {
            tl.add(() => this.setScaleToFitMaxWidth(from, to));
        }

        tl.add(() => {
            this.updateTextLayout();
        });
        tl.to(this, {duration: duration, currentCountValue: to});

        return tl;
    }

    public playEndEffect(): gsap.core.Timeline {
        return (<gsap.core.Timeline><unknown>this._endEffect.getAnimationAutoShowHide(true, true));
    }

    private updateText(): void {
        if (this.currentCountValue - this._prevValue > 0.01) {
            this.setAmount(this.currentCountValue);
            this._prevValue = this.currentCountValue;
        }
    }

    private setAmount(value: number): void {
        const newString: string = Game.apiPlugin.currency.formatValue(value);
        if (newString.length != this._amountText.text.length) {
            this._amountText.text = newString;
            this.updateTextLayout();
        } else {
            this._amountText.text = newString;
        }
    }

    private updateTextLayout(): void {
        this._amountText.position.set(-this._amountText.width * this._anchor.x, -this._amountText.height * this._anchor.y);
    }

    private setScaleToFitMaxWidth(from: number, to: number): void {
        let textScale: number = 1;
        if (this._maxWidth != undefined) {
            this._amountText.scale.set(1);
            this.setAmount(to);
            textScale = Math.min(this._maxWidth / this._amountText.width, 1);
            this.setAmount(from);
        }
        this._amountText.scale.set(textScale);
    }
}